import { Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { PrimaryButton, SecondaryButton } from "@/components/StandardButton";
import { useNavigate, Link } from "react-router-dom";
import { SectionHeader, BodyText } from "@/components/ui/typography";
import { Badge } from "@/components/ui/badge";

const plans = [
  {
    name: "Starter",
    price: 39,
    description: "For HR teams up to 50 employees",
    features: [
      "100 submissions/month",
      "2 department channels",
      "Basic sentiment analysis",
      "Email support",
    ],
    popular: false,
  },
  {
    name: "Professional",
    price: 79,
    description: "For HR teams of 50-500 employees",
    features: [
      "Unlimited submissions",
      "10 department channels",
      "Advanced AI insights & alerts",
      "HR response templates",
      "Priority support",
    ],
    popular: true,
  },
  {
    name: "Business",
    price: 149,
    description: "For enterprise People & Culture teams",
    features: [
      "Unlimited everything",
      "White-label for your brand",
      "HRIS integrations",
      "Executive dashboards",
      "4-hour support SLA",
    ],
    popular: false,
  },
];

export const PricingPreview = () => {
  const navigate = useNavigate();

  return (
    <section className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
      <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8 md:space-y-12">
        <div className="text-center space-y-2 max-w-3xl mx-auto">
          <SectionHeader className="text-foreground px-2">
            Pricing Built for HR Budgets
          </SectionHeader>
          <BodyText className="text-muted-foreground px-2">
            Flat monthly pricing. No per-employee fees. No annual contracts.
          </BodyText>
        </div>

        <div className="grid md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              className={`relative border bg-card p-5 sm:p-6 md:p-8 flex flex-col ${
                plan.popular
                  ? "border-primary shadow-lg ring-2 ring-primary/20"
                  : "border-border"
              }`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs">
                  Most Popular
                </Badge>
              )}

              <div className="space-y-3 sm:space-y-4 flex-1">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground">
                    {plan.name}
                  </h3>
                  <BodyText variant="secondary" className="mt-1 text-sm">
                    {plan.description}
                  </BodyText>
                </div>

                <div className="flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-bold text-foreground">
                    ${plan.price}
                  </span>
                  <span className="text-muted-foreground text-sm">/month</span>
                </div>

                <ul className="space-y-2.5 sm:space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-border">
                {plan.popular ? (
                  <PrimaryButton
                    className="w-full min-h-[48px] touch-manipulation"
                    onClick={() => navigate("/auth?mode=signup")}
                  >
                    Start Free Trial
                  </PrimaryButton>
                ) : (
                  <SecondaryButton
                    className="w-full min-h-[48px] touch-manipulation"
                    onClick={() => navigate("/auth?mode=signup")}
                  >
                    Start Free Trial
                  </SecondaryButton>
                )}
              </div>
            </Card>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/pricing"
            className="text-sm text-primary hover:underline font-medium min-h-[44px] inline-flex items-center touch-manipulation"
          >
            View full feature comparison →
          </Link>
        </div>
      </div>
    </section>
  );
};
